// Heatmap timeline strip — the clip editor and playhead living on the heatmap (it replaced
// the separate clip-editor track). Pure, no Angular. Times in seconds; px are content px
// of the bin grid (t at t/15*step, see heatmap-zoom.ts).

import { BIN_SEC, clampStep, type ViewWindow, type ZoomLimits } from './heatmap-zoom.js';

export const MIN_CLIP_SEC = 0.1; // same floor as setClipTimes
export const CLIP_ZOOM_PAD_SEC = 30; // «zoom to clip» = clip ±30s, the old editor's window
export const FOLLOW_LEAD = 0.05; // page-flip puts the playhead 5% in from the left

export interface ClipRange {
  start: number;
  end: number;
}

export interface HandleZone {
  left: number;
  width: number;
}

/** Hit zones of the clip's start / end handles. Each opens OUTWARD by `out` px and reaches
 *  inward at most `inMax` px — never past the clip's middle — so the two never overlap and
 *  a 2px clip still has two grabbable sides. */
export function clipHandleZones(startPx: number, endPx: number, out: number, inMax: number): { start: HandleZone; end: HandleZone } {
  const inner = Math.max(0, Math.min(inMax, (endPx - startPx) / 2));
  return {
    start: { left: startPx - out, width: out + inner },
    end: { left: endPx - inner, width: inner + out },
  };
}

/** One clip edge moved to `t`, the other kept. Clamped to [0, endSec] (the dragged edge only)
 *  and to MIN_CLIP_SEC from the other edge. */
export function moveClipEdge(clip: ClipRange, edge: 'start' | 'end', t: number, endSec: number): ClipRange {
  if (edge === 'start') {
    const start = Math.max(0, Math.min(t, endSec, clip.end - MIN_CLIP_SEC));
    return { start, end: clip.end };
  }
  const end = Math.max(clip.start + MIN_CLIP_SEC, Math.min(t, endSec));
  return { start: clip.start, end };
}

/** `t` pulled onto the nearest target within `snapPx` screen px, else `t` unchanged. */
export function snapTime(t: number, targets: number[], pxPerSec: number, snapPx: number): number {
  if (!(pxPerSec > 0)) return t;
  let best = t;
  let bestPx = snapPx;
  for (const target of targets) {
    const px = Math.abs(target - t) * pxPerSec;
    if (px <= bestPx) {
      best = target;
      bestPx = px;
    }
  }
  return best;
}

/** A playhead past the last bin is drawn at the end, so it counts as visible there. */
export function isTimeVisible(t: number, view: ViewWindow, endSec: number): boolean {
  const c = Math.min(Math.max(0, t), endSec);
  return c >= view.start && c <= view.end;
}

function clampViewStart(start: number, span: number, endSec: number): number {
  return Math.min(Math.max(0, start), Math.max(0, endSec - span));
}

/** View start that brings `range` into view, or null when it's already fully visible (or
 *  can't move). Centred; a range longer than 80% of the view starts at 10% instead. */
export function ensureVisibleStart(view: ViewWindow, range: ClipRange, endSec: number): number | null {
  const span = view.end - view.start;
  if (!(span > 0) || !(endSec > 0)) return null;
  const s = Math.min(Math.max(0, range.start), endSec);
  const e = Math.min(Math.max(s, range.end), endSec);
  if (s >= view.start && e <= view.end) return null;
  const want = e - s > span * 0.8 ? s - span * 0.1 : (s + e) / 2 - span / 2;
  const start = clampViewStart(want, span, endSec);
  return Math.abs(start - view.start) > span * 1e-3 ? start : null;
}

/** Page-flip while playing: only when the playhead WAS visible and has just left the view —
 *  a view the user panned away on purpose is left alone. Forward → the playhead lands
 *  FOLLOW_LEAD in from the left; backward (J, ←) → centred. */
export function followStart(wasVisible: boolean, t: number, view: ViewWindow, endSec: number): number | null {
  if (!wasVisible) return null;
  const span = view.end - view.start;
  if (!(span > 0) || !(endSec > 0)) return null;
  const c = Math.min(Math.max(0, t), endSec);
  let want: number;
  if (c > view.end) want = c - span * FOLLOW_LEAD;
  else if (c < view.start) want = c - span / 2;
  else return null;
  const start = clampViewStart(want, span, endSec);
  return Math.abs(start - view.start) > span * 1e-3 ? start : null;
}

/** «Zoom to clip» window: the clip ±pad, inside the stream. */
export function clipZoomWindow(clip: ClipRange, endSec: number, pad = CLIP_ZOOM_PAD_SEC): ViewWindow {
  const start = Math.max(0, Math.min(clip.start, endSec) - pad);
  const end = Math.min(endSec, Math.max(clip.end, clip.start) + pad);
  return { start, end: Math.max(start, end) };
}

/** Step + scrollLeft that show `win` centred. Step rounded DOWN to 1/64 px (like
 *  zoomToWindow) so the window fits; the zoom limits may widen it — it stays centred. */
export function centeredZoom(win: ViewWindow, innerWidth: number, limits: ZoomLimits): { step: number; scrollLeft: number } {
  const span = Math.max(1, win.end - win.start);
  const step = clampStep(Math.floor((innerWidth + 1) * BIN_SEC / span * 64) / 64, limits);
  const mid = (win.start + win.end) / 2;
  return { step, scrollLeft: Math.max(0, mid / BIN_SEC * step - (innerWidth + 1) / 2) };
}

/** «0:40», «1:02:05». */
export function formatClipDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor(s % 3600 / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** «01:02:05.4» — drag readouts at deep zoom need tenths. */
export function formatTimecode(sec: number): string {
  const tenths = Math.max(0, Math.round(sec * 10));
  const s = Math.floor(tenths / 10);
  const p = (v: number) => String(v).padStart(2, '0');
  return `${p(Math.floor(s / 3600))}:${p(Math.floor(s % 3600 / 60))}:${p(s % 60)}.${tenths % 10}`;
}
